import React, { useEffect, useState, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { lifestyleApi } from "../apiService/api";
import { useParams } from "react-router-dom";
import "./Glucose.css";
import "../index.css";
import { getUrlParams } from "../apiService/timespam";
import SectionHeader from "../Components/SectionHeader";
import { navigateWithFai } from "../utils/Utils";

// Shimmer helpers (same pattern as Highchart, Agingpace, AgeCompare)
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const GlucoseSkeleton = () => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        backgroundColor: "var(--bg-card)",
        borderRadius: "20px",
        border: "1px solid var(--border-subtle)",
      }}
    >
      {/* SectionHeader skeleton */}
      <div style={{ padding: "12px 16px 0 16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {/* Icon circle */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 32,
              height: 32,
              borderRadius: 999,
            }}
          />

          {/* Title + subtitle */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div
              style={{
                ...shimmerBaseStyle,
                height: 18,
                width: "60%",
                borderRadius: 8,
              }}
            />
            <div
              style={{
                ...shimmerBaseStyle,
                height: 14,
                width: "40%",
                borderRadius: 8,
              }}
            />
          </div>

          {/* Info icon circle */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 20,
              height: 20,
              borderRadius: 999,
            }}
          />
        </div>
      </div>

      {/* Chart skeleton */}
      <div
        style={{
          marginTop: 12,
          padding: "0 16px 20px 16px",
        }}
      >
        <div
          style={{
            ...shimmerBaseStyle,
            height: 180,
            borderRadius: 16,
          }}
        />
      </div>
    </div>
  );
};

const Glucose = () => {
  const [data, setData] = useState(null);
  const chartRef = useRef(null);
  const { goqiiUserId } = useParams();

  useEffect(() => {
    const { encryptedId } = getUrlParams();

    if (!encryptedId) return;

    lifestyleApi
      .glucoseData(encryptedId)
      .then((response) => {
        if (response?.data?.code === 200) {
          setData(response.data.data);
        }
      })
      .catch((err) => console.error("Error loading glucose data:", err));
  }, []);

  if (!data) return <GlucoseSkeleton />;


  // Highcharts options
  const options = {
    chart: {
      type: "spline",
      height: 80,

      spacingRight: 10,
      spacingLeft: 20,

      backgroundColor: "transparent",
    },
    title: { text: null },
    legend: { enabled: false },
    xAxis: {
      categories: data.xAxis,
      tickLength: 0,
      lineWidth: 1,
      lineColor: "var(--border-subtle)",
      labels: {
        style: {
          fontFamily: "Open Sans, sans-serif",
          fontSize: "11px",
          color: "var(--text-primary)",
        },
        rotation: 0,
        y: 18,
      },
      minorGridLineWidth: 0,
      gridLineWidth: 0,
    },
    yAxis: {
      title: { text: null },
      labels: {
        enabled: false,
        style: {
          fontFamily: "Open Sans, sans-serif",
          fontSize: "11px",
        },
      },
      tickInterval: 10,
      lineWidth: 0,
      lineColor: "var(--border-subtle)",
      maxPadding: 0.1,
      gridLineColor: "transparent",
      gridLineWidth: 0,
    },
    tooltip: {
      enabled: false,
      shared: true,
      headerFormat: "",
      pointFormat: "<b>{point.y}</b>",
      style: {
        fontFamily: "Open Sans, sans-serif",
        fontSize: "12px",
      },
    },
    plotOptions: {
      series: {
        marker: { enabled: false },
        lineWidth: 3,
        states: { hover: { lineWidth: 3.5 } },
        dataLabels: {
          enabled: true,
          formatter: function () {
            return this.y;
          },
          style: {
            fontFamily: "Open Sans, sans-serif",
            fontSize: "11px",
            fontWeight: "600",
            textOutline: "none",
            color: "var(--text-primary)",
          },
          y: -18,
        },
      },
    },
    series: [
      {
        name: data.title,
        data: data.yAxis,
        color: "#2DA64F",
      },
    ],
    credits: { enabled: false },
    responsive: {
      rules: [
        {
          condition: { maxWidth: 474 },
          chartOptions: {
            xAxis: {
              labels: {
                rotation: -45,
                align: "right",
                y: 10,
                style: {
                  fontFamily: "Open Sans, sans-serif",
                  fontSize: "11px",
                },
              },
            },
            chart: { height: 150 },
          },
        },
      ],
    },
  };

  return (
        <div
            style={{
                width: "100%",
                backgroundColor: "var(--bg-card)",
                borderRadius: "20px",
                border: "1px solid var(--border-subtle)",
            }}
        >
            <SectionHeader
                icon="https://appcdn.goqii.com/storeimg/6490_1764052397.png"
                title={data.title}
                subtitle={data.heading}
               
            />

            <HighchartsReact highcharts={Highcharts} options={options} />
        </div>
    );
};

export default Glucose;
